import { tokenStorage, authTokenService } from "./authToken.service";

let isRefreshing = false;
let failedQueue = [];

function processQueue(error, token = null) {
  failedQueue.forEach(({ resolve, reject }) => {
    error ? reject(error) : resolve(token);
  });
  failedQueue = [];
}

/**
 * Attaches request + response interceptors to the provided axios instance.
 *
 * Request:  inject Authorization: Bearer {accessToken} from localStorage
 * Response: on 401 → try refresh token → retry original request
 *           if refresh fails → clear tokens + session → redirect to "/"
 *
 * Call once in main.js: setupAxiosInterceptors(axios)
 */
export function setupAxiosInterceptors(axiosInstance) {
  // ── Request: attach stored access token ───────────────────────────────────
  axiosInstance.interceptors.request.use((config) => {
    const token = tokenStorage.getAccessToken();
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  });

  // ── Response: handle 401 → refresh → retry ────────────────────────────────
  axiosInstance.interceptors.response.use(
    (response) => response,
    async (error) => {
      const originalRequest = error.config;

      if (error.response?.status !== 401 || originalRequest._retry) {
        return Promise.reject(error);
      }

      // Queue concurrent requests while a refresh is in progress
      if (isRefreshing) {
        return new Promise((resolve, reject) => {
          failedQueue.push({ resolve, reject });
        })
          .then((token) => {
            originalRequest.headers.Authorization = `Bearer ${token}`;
            return axiosInstance(originalRequest);
          })
          .catch((err) => Promise.reject(err));
      }

      originalRequest._retry = true;
      isRefreshing = true;

      const refreshToken = tokenStorage.getRefreshToken();
      if (!refreshToken) {
        isRefreshing = false;
        tokenStorage.clearTokens();
        sessionStorage.clear();
        window.location.href = "/";
        return Promise.reject(error);
      }

      try {
        const response = await authTokenService.refreshAccessToken(refreshToken);
        const newToken = response.data.token;

        tokenStorage.setTokens(newToken, null); // refresh token stays unchanged
        processQueue(null, newToken);

        originalRequest.headers.Authorization = `Bearer ${newToken}`;
        return axiosInstance(originalRequest);
      } catch (refreshError) {
        processQueue(refreshError, null);
        tokenStorage.clearTokens();
        sessionStorage.clear();
        window.location.href = "/";
        return Promise.reject(refreshError);
      } finally {
        isRefreshing = false;
      }
    }
  );
}
